import { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { useField } from 'formik';
import { Box } from '@/components/Box/Box';
import { InputError } from '../InputError/InputError';
import { ResponseErrorType } from '@/types/sharedTypes';
import { Label, StyledPhoneInput } from './styles';
import 'react-phone-input-2/lib/high-res.css';
import { getFieldError } from '@/helpers/formHelpers/formHelpers';
import noop from 'lodash/noop';
import { Input } from '../Input/Input';
import uniqueId from 'lodash/uniqueId';

interface ChangeCountryType {
  countryCode: string;
  dialCode: string;
  format: string;
  name: string;
}

interface FormikPhoneInputType {
  name: string;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  apiError?: ResponseErrorType;
  onEnterKeyPress?: () => void;
  hint?: ReactNode;
}

const DEFAULT_COUNTRY_CODE = 'us';
const DEFAULT_ID = uniqueId();

export const FormikPhoneInput = ({
  name,
  label,
  disabled,
  apiError,
  onEnterKeyPress = noop,
  hint,
}: FormikPhoneInputType) => {
  const [field, meta, { setValue, setError, setTouched }] = useField(name);
  const [key, setKey] = useState(DEFAULT_ID);

  const { error, apiError: apiErrorMessage, hasAnyError } = getFieldError(name, meta, apiError);

  const changePhone = (newPhone: string, country: ChangeCountryType, event: ChangeEvent<HTMLInputElement>) => {
    setValue(newPhone);

    // Country was changed in dropdown -> reset phone number value and error
    if (event.type === 'click') {
      setValue(country.dialCode);

      if (error) {
        setTouched(false);
        setError('');
      }
    }
  };

  useEffect(() => {
    if (!field?.value) {
      // Note: "react-phone-input-2" does not update country flag/phone mask on value clear(reset) - force re-render in such case.
      setKey(uniqueId());
    }
  }, [field?.value]);

  return (
    <Box flexDirection='column' rowGap='4px' width='100%' key={key}>
      <Label>{label}</Label>
      <Box flexDirection='column'>
        {disabled ? (
          <Input
            name={field.name}
            value={field.value}
            disabled
            placeholder='Enter Mobile Phone Number'
            hasErrorComponent={false}
          />
        ) : (
          <StyledPhoneInput
            inputProps={{
              name: field.name,
            }}
            country={DEFAULT_COUNTRY_CODE}
            value={field.value}
            onEnterKeyPress={onEnterKeyPress}
            error={hasAnyError}
            onChange={changePhone}
            onBlur={field.onBlur}
          />
        )}

        <InputError error={error} apiError={apiErrorMessage} hint={hint} />
      </Box>
    </Box>
  );
};
